import { ATTENDEE_STATUS } from '@alucio/aws-beacon-amplify/src/API';
import { useInterval } from '@alucio/lux-ui';
import React, { useState, useEffect } from 'react';
import { MODE_TYPE, useParticipants, useVideoContext } from '../../../main';
import subSeconds from 'date-fns/subSeconds';
import isAfter from 'date-fns/isAfter';
import compareAsc from 'date-fns/compareAsc';
import Attendee from '../Attendee/Attendee';
import { ScrollViewContainer } from './ScrollViewContainer';
import { View } from 'react-native';
import useUpdateVisibleUsers from '../../../hooks/useUpdatedVisibleUsers';
import AttendeesOrder, { useOrderedAttendees } from '../../AlucioVideoProvider/AttendeesOrderProvider/AttendeesOrder';
import ConfirmationAttendeeShareScreen from '../../Attendee/Confirmations/ShareScreen';
const MAX_ATTENDEES = 19;
export function AttendeesWrapper(props) {
    var _a;
    const { currentAttendees, localPhoneAudio } = props;
    const participants = useParticipants();
    const { room } = useVideoContext();
    const localParticipantId = (_a = room.localParticipant) === null || _a === void 0 ? void 0 : _a.identity.split('.')[0];
    const [disconnectedAttendees, setDisconnectedAttendees] = useState([]);
    // In Guest Mode all attendee information is derived from participants
    const formattedAttendees = participants.map((participant) => {
        const participantParts = participant.identity.split('.');
        return {
            id: participantParts[0],
            type: participantParts[1],
            identity: participant.identity,
            status: ATTENDEE_STATUS.CONNECTED,
            name: participantParts.slice(participantParts[1] === 'phone' ? 3 : 2).join('.'),
        };
    });
    // The attendee might have lost their "main connection" but they're still connected by phone
    // If this is the case, we still want to show them in the slide roll of participants.
    const connectedAttendees = formattedAttendees.filter((attendee) => {
        if (attendee.type === 'video') {
            return true;
        }
        // If there's a participant with the same id of the localParticipant, it means it's the phone of the local
        // and we don't want to add it as another participant in the participant's list so we ignore it.
        if (attendee.id === localParticipantId) {
            return false;
        }
        // If we're here, it means that the attendee of the current iteration is an audio participant
        // so we check if their video is connected. If not, return this as a participant.
        const isTheirVideoConnected = formattedAttendees.find(({ id, type }) => id === attendee.id && type === 'video');
        return !isTheirVideoConnected;
    });
    const allPendingAttendees = Object.keys(currentAttendees).map((identity) => {
        return Object.assign({ identity: identity, type: 'video' }, currentAttendees[identity]);
    }).filter((attendee) => {
        return attendee.status === ATTENDEE_STATUS.PENDING || attendee.status === ATTENDEE_STATUS.ACCEPTED;
    });
    const getConnectedDisconnectedAttendees = (attendee) => {
        // TODO: we need to check what is the proper value for this, in order to avoid
        // the user left the message when the user is still waiting
        const last40Seconds = subSeconds(new Date(), 40);
        return attendee.reduce((acc, attendee) => {
            if (attendee.lastSeenAt && isAfter(new Date(attendee.lastSeenAt), last40Seconds)) {
                acc[0].push(attendee);
            }
            else {
                attendee.status = ATTENDEE_STATUS.DISCONNECTED;
                acc[1].push(attendee);
            }
            return acc;
        }, [[], []]);
    };
    const [connectedPendingAttendees, disconnectedPendingAttendees] = getConnectedDisconnectedAttendees(allPendingAttendees);
    useEffect(() => setDisconnectedAttendees(disconnectedPendingAttendees), []);
    const allAttendees = [...connectedAttendees, ...connectedPendingAttendees];
    allAttendees.sort((a, b) => {
        var _a, _b;
        if (a.id === 'host') {
            return -1;
        }
        else if (b.id === 'host') {
            return 1;
        }
        else if (a.status === ATTENDEE_STATUS.PENDING && b.status === ATTENDEE_STATUS.PENDING) {
            return compareAsc(new Date((_a = a === null || a === void 0 ? void 0 : a.createdAt) !== null && _a !== void 0 ? _a : '1900-01-01'), new Date((_b = b === null || b === void 0 ? void 0 : b.createdAt) !== null && _b !== void 0 ? _b : '1900-01-01'));
        }
        else if (a.status === ATTENDEE_STATUS.PENDING) {
            return -1;
        }
        else if (b.status === ATTENDEE_STATUS.PENDING) {
            return 1;
        }
        else {
            return a.name.localeCompare(b.name);
        }
    });
    // Check on regular intervals to see if any attendees lastChangedAt has exceeded timeout window
    useInterval(() => {
        const disconnectedPendingAttendees = getConnectedDisconnectedAttendees(allPendingAttendees)[1];
        const currAttendeeSet = new Set(disconnectedPendingAttendees.map((x) => x.id));
        if (currAttendeeSet.size !== disconnectedAttendees.length ||
            !disconnectedAttendees.reduce((allPresent, attendee) => allPresent && currAttendeeSet.has(attendee.id), true)) {
            // Setting state causes a re-render
            setDisconnectedAttendees(disconnectedPendingAttendees);
        }
    }, 2000);
    const connectedOrAccepted = allAttendees.filter((x) => x.status === ATTENDEE_STATUS.ACCEPTED || x.status === ATTENDEE_STATUS.CONNECTED);
    const handleCallAccepted = (attendeeId, name) => {
        if (connectedOrAccepted.length >= MAX_ATTENDEES) {
            console.warn('Max attendee limit reached. Rejecting accept');
        }
        else {
            props.onAcceptCall && props.onAcceptCall(attendeeId, name);
        }
    };
    const isRoomFull = connectedOrAccepted.length >= MAX_ATTENDEES;
    return (React.createElement(AttendeesOrder, null,
        React.createElement(Attendees, { attendees: [...allAttendees, ...disconnectedPendingAttendees], attendeesPerPage: props.attendeesPerPage, localPhoneAudio: localPhoneAudio, isRoomFull: isRoomFull, handleCallAccepted: handleCallAccepted, isFullScreen: props.isFullScreen, mode: props.mode, onAcceptCall: props.onAcceptCall, onDenyCall: props.onDenyCall, onRemoveFromCall: props.onRemoveFromCall, onAttendeeConnected: props.onAttendeeConnected })));
}
function Attendees(props) {
    const { attendees, attendeesPerPage, mode, isFullScreen, isRoomFull, localPhoneAudio, handleCallAccepted, onDenyCall, onRemoveFromCall, onAttendeeConnected, } = props;
    // Find the host to stick it
    const host = attendees.find((e) => e.id === 'host');
    // Pinned elements
    const pinnedElements = attendees.filter((e) => [
        ATTENDEE_STATUS.PENDING,
        ATTENDEE_STATUS.DISCONNECTED,
        ATTENDEE_STATUS.ACCEPTED,
    ].includes(e.status));
    // attendees
    const attendeeForControls = attendees
        .filter((e) => e.id !== 'host' &&
        ![
            ATTENDEE_STATUS.PENDING,
            ATTENDEE_STATUS.DISCONNECTED,
            ATTENDEE_STATUS.ACCEPTED,
        ].includes(e.status));
    const orderedAttendees = useOrderedAttendees(attendeeForControls);
    // Change the debugMode to true, if you want to have track of the subscriptions
    const { updateUsers } = useUpdateVisibleUsers({
        attendees: isFullScreen ? [] : host ? [host, ...orderedAttendees] : orderedAttendees,
        debugMode: false,
        mode,
    });
    const attendeeControls = orderedAttendees.map((e) => (React.createElement(Attendee, { key: `accept_deny_${e.identity}`, status: e.status, attendeeId: e.id, showHostControls: mode === 'Host', identity: e.identity, isRoomFull: isRoomFull, localPhoneAudio: localPhoneAudio, name: e.name, onAcceptCall: handleCallAccepted, onDenyCall: onDenyCall, onRemoveFromCall: onRemoveFromCall, onAttendeeConnected: onAttendeeConnected })));
    return (React.createElement(React.Fragment, null,
        host &&
            React.createElement(View, null,
                React.createElement(Attendee, { key: `accept_deny_${host.identity}`, status: host.status, attendeeId: host.id, showHostControls: false, identity: host.identity, isRoomFull: isRoomFull, localPhoneAudio: localPhoneAudio, name: host.name, onAcceptCall: handleCallAccepted, onDenyCall: onDenyCall, onRemoveFromCall: onRemoveFromCall, onAttendeeConnected: onAttendeeConnected })),
        pinnedElements.map((pinned, index) => (React.createElement(View, { key: `accept_deny_${index}` },
            React.createElement(Attendee, { key: `accept_deny_${index}`, status: pinned.status, attendeeId: pinned.id, showHostControls: mode === 'Host', identity: pinned.identity, isRoomFull: isRoomFull, localPhoneAudio: localPhoneAudio, name: pinned.name, onAcceptCall: handleCallAccepted, onDenyCall: onDenyCall, onRemoveFromCall: onRemoveFromCall, onAttendeeConnected: onAttendeeConnected })))),
        React.createElement(ScrollViewContainer
        // the view port minus the pinned elements
        , { 
            // the view port minus the pinned elements
            attendeesPerPage: attendeesPerPage - pinnedElements.length, participants: attendeeControls.length, attendeeControls: attendeeControls, isHost: mode === MODE_TYPE.HOST, selectedPage: (visibleAttendees, page) => updateUsers(visibleAttendees, page) }, attendeeControls),
        React.createElement(ConfirmationAttendeeShareScreen, null)));
}
//# sourceMappingURL=Attendees.js.map